/**
 * useMonitorSettings — admin/founder access to the singleton
 * raela_monitor_settings row that controls escalation thresholds,
 * strike decay windows, and send-cooldown behavior.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MonitorSettings {
  id: number;
  warn_decay_days: number;
  crisis_decay_days: number | null;
  block_decay_days: number | null;
  mute_threshold: number;
  mute_duration_hours: number;
  suspend_threshold: number;
  high_priority_threshold: number;
  critical_priority_threshold: number;
  send_cooldown_seconds: number;
  send_cooldown_window_hours: number;
  updated_at: string;
  updated_by: string | null;
}

export const useMonitorSettings = () => {
  const [settings, setSettings] = useState<MonitorSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("raela_monitor_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle();
    if (err) setError(err.message);
    setSettings((data as MonitorSettings | null) ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (patch: Partial<MonitorSettings>) => {
      const { error: err } = await supabase
        .from("raela_monitor_settings")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", 1);
      if (!err) await load();
      return { error: err?.message ?? null };
    },
    [load],
  );

  return { settings, loading, error, reload: load, save };
};
